export type OutcomeSteps = { steps?: { step: string; succeeded: boolean }[] } | null | undefined

/** Whether the onboarding service reported the named step as failed on a submit outcome. */
export function hasFailedOutcomeStep(outcome: OutcomeSteps, step: string): boolean {
  return outcome?.steps?.some((s) => s.step === step && s.succeeded === false) ?? false
}

/**
 * Whether a submit created the work order but left the linked pre-verification as it was: the onboarding
 * service reports that as a failed "PreVerification" step on an otherwise successful outcome.
 */
export function hasFailedPreVerificationStep(outcome: OutcomeSteps): boolean {
  return hasFailedOutcomeStep(outcome, "PreVerification")
}

export type OnboardingSubmitFeedback = {
  variant: "default" | "destructive"
  titleKey: "success" | "warning" | "error"
  descriptionKey:
    | "onboardingSuccess"
    | "preVerificationCloseFailed"
    | "atsAcceptFailedAfterOnboarding"
    | "vmsAcceptFailedAfterOnboarding"
    | "clientRequestCloseFailedAfterOnboarding"
    | "clientRequestLinkFailedAfterOnboarding"
  /** Whether the source request was accepted, so its row can show RequestAccepted before the refresh lands. */
  sourceAccepted: boolean
}

/**
 * The one toast a successful onboarding submit gets, and whether its source request was accepted. The toaster
 * shows a single toast at a time, so a submit with several failed steps reports the one that matters most.
 */
export function getOnboardingSubmitFeedback(entityType: string | undefined, outcome: OutcomeSteps): OnboardingSubmitFeedback {
  if (entityType === "AtsPlacementRequest" && hasFailedOutcomeStep(outcome, "AtsAccept")) {
    return { variant: "destructive", titleKey: "error", descriptionKey: "atsAcceptFailedAfterOnboarding", sourceAccepted: false }
  }
  if (entityType === "VmsRequest" && hasFailedOutcomeStep(outcome, "VmsRequestLink")) {
    return { variant: "destructive", titleKey: "error", descriptionKey: "vmsAcceptFailedAfterOnboarding", sourceAccepted: false }
  }
  if (hasFailedPreVerificationStep(outcome)) {
    // A client request is its own pre-verification, so a close that failed left the source itself unaccepted.
    return entityType === "ClientRequest"
      ? { variant: "destructive", titleKey: "warning", descriptionKey: "clientRequestCloseFailedAfterOnboarding", sourceAccepted: false }
      : { variant: "destructive", titleKey: "warning", descriptionKey: "preVerificationCloseFailed", sourceAccepted: true }
  }
  // The close does not depend on the link, so the request is accepted, but without the work order ids its job
  // description never reaches the work order.
  if (entityType === "ClientRequest" && hasFailedOutcomeStep(outcome, "RequestLink")) {
    return { variant: "destructive", titleKey: "warning", descriptionKey: "clientRequestLinkFailedAfterOnboarding", sourceAccepted: true }
  }
  return { variant: "default", titleKey: "success", descriptionKey: "onboardingSuccess", sourceAccepted: true }
}
